/**
 * Sword parts (lane SWORD, user 2026-10-11: direction C as the base, weapons very varied): a jian (กระบี่) is built
 * from parts — blade profile, guard, grip, pommel, tassel, scabbard — each in a material, so a sword is a small
 * data record (`SwordSpec`) and the figure paints it pixel by pixel at full resolution (paint.ts). Every part kind
 * has a registry here; shapes.ts and materials.ts register the parts, catalog.ts the named swords.
 *
 * Painting coordinates (see paint.ts `raster`): `a` runs along the sword in art pixels from the hand (the guard's
 * back face at 0, the blade toward +a, the pommel toward −a); `c` runs across it in fine (canvas) pixels; `u` is
 * the across side toward the light (+1 lit … −1 shaded) for rounded parts.
 */

import type { Rarity } from '@nine/shared';

/** Four tones of a material: glint, light, mid, dark. All from the master palette (render/truepx/palette.ts). */
export type Ramp = readonly [string, string, string, string];

/** A named sword: its parts and materials. Ids index the registries below. */
export interface SwordSpec {
  id: string;
  /** Shown in the studio's catalogue. */
  label: string;
  /** The item rarity it is drawn for (its shown name: `RARITY_LABELS`). */
  rarity: Rarity;
  blade: {
    profile: string;
    steel: string;
    /** Down the middle: a ridge line, a fuller groove on the shaded face, twin grooves, or a plain two-tone face. */
    deco?: 'ridge' | 'fuller' | 'twin' | 'plain';
    /** A line inlaid down the ridge over the blade's first 60 % (a colour). */
    inlay?: string;
    /** Folded-steel banding across the faces (the light band's colour). */
    pattern?: string;
    /** Seven-star dots down the ridge (a colour). */
    stars?: string;
    /** A hardened edge: the edges in this colour. */
    edge?: string;
  };
  guard: { shape: string; metal: string; gem?: string };
  grip: { wrap: string; silk: string };
  pommel: { shape: string; metal?: string; gem?: string };
  tassel?: { style: string; silk: string; bead?: string };
  scabbard: { style: string; body: string; metal?: string; carry?: 'hip' | 'back' };
}

export interface BladeProfile {
  /** Art px from the hand to the point. */
  length: number;
  /** Half-width (fine px) along the blade, `t` 0 at the guard … 1 at the point. */
  half: (t: number) => number;
}

/** A guard: where it ends toward the blade (art px), its widest half (fine px), and its shape → a light value. */
export interface GuardShape {
  end: number;
  maxHalf: number;
  /** `ac` = |c|; returns a tone (−1 dark … 1 glint) or nothing outside the shape; 'gem' marks the stone's pixels. */
  at: (a: number, ac: number, u: number) => number | 'gem' | 'eye' | undefined;
}

export interface PommelShape {
  /** Length along the sword (art px) and widest half (fine px). */
  len: number;
  maxHalf: number;
  /** `t` 0 at the grip … 1 at the end; returns a tone or nothing. */
  at: (t: number, ac: number, u: number, lenPx: number) => number | 'gem' | undefined;
  /** Where the tassel hangs from, as a fraction of `len`. */
  hang: number;
}

export interface GripWrap {
  /** The wrap's colour at a sample (`base`, `light`: the grip's two silk tones). */
  at: (a: number, c: number, u: number, base: string, light: string, res: number) => string;
}

export interface TasselStyle {
  /** Paints the tassel hanging from `top` (fine px) under gravity; `sway(i, n)` is the figure's flutter (fine px). */
  draw: (put: (x: number, y: number, col: string) => void, top: { x: number; y: number }, silk: Ramp, bead: string | undefined, sway: (i: number, n: number) => number) => void;
}

export interface ScabbardStyle {
  /** Extra half-width over the blade's root (fine px). */
  wider: number;
  /** Locket length (art px) at the mouth. */
  locket: number;
  /** Bands: positions as fractions of the length. */
  bands: readonly number[];
  /** A Han scabbard slide (璏) on the face: from … to, fractions of the length. */
  slide?: readonly [number, number];
  chape: 'round' | 'trap' | 'cloud' | 'point' | 'flare';
  /** Chape length (art px). */
  chapeLen: number;
  /** A cord wrap just under the locket (art px long). */
  wrap?: number;
}

export const STEELS = new Map<string, Ramp>();
export const METALS = new Map<string, Ramp>();
/** Scabbard bodies: lacquers, leathers, ray skins (gloss, light, base, dark). */
export const BODIES = new Map<string, Ramp>();
/** Silks for grips and tassels (glint, light, base, dark). */
export const SILKS = new Map<string, Ramp>();
export const BLADES = new Map<string, BladeProfile>();
export const GUARDS = new Map<string, GuardShape>();
export const POMMELS = new Map<string, PommelShape>();
export const GRIPS = new Map<string, GripWrap>();
export const TASSELS = new Map<string, TasselStyle>();
export const SCABBARDS = new Map<string, ScabbardStyle>();
export const SWORDS = new Map<string, SwordSpec>();

export const registerSword = (s: SwordSpec): void => void SWORDS.set(s.id, s);
